import ColorSelector from '@components/ColorSelector/ColorSelector';
import { SideEditorNodeKey } from '@components/EmailSignaturev2/types';
import { FormSelector, FormSelectorGroup } from '@components/FormSelector/FormSelector';
// types
import { SideEditorScrollType } from '@components/SideEditor/SideEditor';
// hooks and context
import { useSideEditorContext } from '@components/SideEditor/utils';
import Toggle from '@components/Toggle/Toggle';
import { emailSignatureSideEditorServicesContext } from '@context/email-signature-services-context';
import useNode from '@hooks/use-node';
// utils
// es-toolkit/compat: lodash isEmpty treats 0/true/null as empty
import { isEmpty } from 'es-toolkit/compat';
import { type FC, useContext, useEffect } from 'react';

// components
import EmailSignatureEditorPanelSection, {
  EmailSignatureEditorPanelSectionContents,
  EmailSignatureEditorPanelSectionHeading,
  EmailSignatureEditorPanelSectionHeadingButton,
} from '../EmailSignatureEditorPanelSection';
import EmailSignatureLockLayer from '../EmailSignatureLockLayer';
import styles from './EmailSignatureEditorDesignPanelSection.module.scss';

const EmailSignatureEditorContactDetailsDesignSection: FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { scrollIntoView, registerNode, panelIndex } = useSideEditorContext();
  const {
    isEverythingEmpty,
    emailSignature,
    emailSignatureDispatch,
    hasEditedFieldWidth,
    isSignatureLocked,
  } = useContext(emailSignatureSideEditorServicesContext);
  const [contactDetailsDesignSectionDiv, contactDetailsDesignSectionDivRef] =
    useNode<HTMLDivElement>();

  useEffect(() => {
    if (!contactDetailsDesignSectionDiv) return;
    registerNode(SideEditorNodeKey.CONTACT_DETAILS_DESIGN_NODE, {
      panelBelongTo: panelIndex,
      node: contactDetailsDesignSectionDiv,
    });
  }, [contactDetailsDesignSectionDiv, panelIndex, registerNode]);

  const disabledInputs =
    (isEmpty(emailSignature.contactDetails) && !isEverythingEmpty) || isSignatureLocked;

  return (
    <EmailSignatureEditorPanelSection ref={contactDetailsDesignSectionDivRef}>
      <EmailSignatureEditorPanelSectionHeading heading={'Details'}>
        {!isSignatureLocked && (
          <EmailSignatureEditorPanelSectionHeadingButton
            buttonText={`${isEmpty(emailSignature.contactDetails) ? 'Add' : 'Edit'} details`}
            onClick={() => {
              scrollIntoView(SideEditorNodeKey.CONTACT_DETAILS_NODE, SideEditorScrollType.START);
            }}
            isEmphasised={isEmpty(emailSignature.contactDetails) && !isEverythingEmpty}
          />
        )}
      </EmailSignatureEditorPanelSectionHeading>

      <EmailSignatureEditorPanelSectionContents
        // only disable the section when the fields are empty, we will show a different ui when the section is locked
        disabled={isEmpty(emailSignature.contactDetails) && !isEverythingEmpty}
      >
        <div className={styles['design-section-option']} data-direction="horizontal">
          <div className={styles['design-section-option-label']}>Display icons</div>
          <Toggle
            disabled={disabledInputs}
            state={emailSignature.design.contactDetailsDisplayIcon}
            onChange={(isChecked) => {
              emailSignatureDispatch({
                type: 'UPDATE_DESIGN_ICON_IN_CONTACT',
                value: { displayIcon: isChecked, changeFieldWidth: !hasEditedFieldWidth },
              });
            }}
          />
        </div>

        {emailSignature.design.contactDetailsDisplayIcon && (
          <div className={styles['design-section-option']} data-direction="vertical">
            <div className={styles['design-section-option-label']}>Icons color</div>
            <div className={styles['design-section-option-multi-steps']}>
              <FormSelectorGroup
                className={styles['design-section-group-form-selector']}
                selectedValue={emailSignature.design.contactDetailsIconUseThemeColor.toString()}
                onChange={(newValue) =>
                  emailSignatureDispatch({
                    type: 'UPDATE_DESIGN_CONTACT_ICON_USE_THEME',
                    value: newValue === 'true' ? true : false,
                  })
                }
                size="sm"
              >
                <FormSelector
                  disabled={disabledInputs}
                  value={'true'}
                  className={styles['design-section-form-selector']}
                >
                  Theme Color
                </FormSelector>
                <FormSelector
                  disabled={disabledInputs}
                  value={'false'}
                  className={styles['design-section-form-selector']}
                >
                  Custom
                </FormSelector>
              </FormSelectorGroup>
              {!emailSignature.design.contactDetailsIconUseThemeColor && (
                <ColorSelector
                  color={emailSignature.design.contactDetailsIconColor}
                  onSelect={(color) =>
                    emailSignatureDispatch({
                      type: 'UPDATE_DESIGN_CONTACT_ICON_COLOR',
                      value: color,
                    })
                  }
                  disabled={disabledInputs}
                />
              )}
            </div>
          </div>
        )}
        {isSignatureLocked && (
          <EmailSignatureLockLayer message="You cannot edit this styling, please contact your admin if you have any questions" />
        )}
      </EmailSignatureEditorPanelSectionContents>
    </EmailSignatureEditorPanelSection>
  );
};

export default EmailSignatureEditorContactDetailsDesignSection;
